import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Button, Card, CardContent, Chip, Snackbar, Stack, Switch, Typography,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import AddAlarmIcon from '@mui/icons-material/AddAlarm';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import ScheduleDialog from '../components/ScheduleDialog';
import StateBoundary from '../components/StateBoundary';
import { useSession } from '../viewmodels/SessionViewModel';
import { useSchedulesViewModel } from '../viewmodels/SchedulesViewModel';
import { formatDateTime } from '../utils/format';

const STATUS_TEXT = {
  started: 'Last run started',
  waiting_busy: 'Waiting: the team was busy, trying again shortly',
  skipped_busy: 'Skipped: the team was busy',
  daily_limit: 'Skipped: daily limit for scheduled runs reached',
  chat_missing: 'Switched off: its chat no longer exists',
  error: 'Could not start',
};

function ScheduleCard({ s, isOwner, busy, onToggle, onRun, onEdit, onDelete }) {
  return (
    <Card sx={{ opacity: s.enabled ? 1 : 0.75 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <div>
            <Typography variant="h6">{s.title}</Typography>
            <Typography color="text.secondary">{`${s.description} (${s.timezone})`}</Typography>
          </div>
          {isOwner && <Switch checked={s.enabled} onChange={onToggle} disabled={busy} inputProps={{ 'aria-label': `Switch ${s.title} on or off` }} />}
        </Stack>
        <Typography sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{s.prompt}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`Sent to ${s.conversation.title}`} />
          {s.enabled && s.next_run_at && <Chip size="small" color="primary" variant="outlined" label={`Next: ${formatDateTime(s.next_run_at)}`} />}
          {!s.enabled && <Chip size="small" label="Off" />}
          {s.last_status && (
            <Chip
              size="small"
              color={['error', 'chat_missing'].includes(s.last_status) ? 'error' : 'default'}
              variant="outlined"
              label={`${STATUS_TEXT[s.last_status] || s.last_status}${s.last_run_at && s.last_status === 'started' ? `: ${formatDateTime(s.last_run_at)}` : ''}`}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {isOwner && <Button size="small" variant="outlined" disabled={busy} onClick={onRun}>Run now</Button>}
          {isOwner && <Button size="small" disabled={busy} onClick={onEdit}>Edit</Button>}
          {isOwner && <Button size="small" color="error" disabled={busy} onClick={onDelete}>Delete</Button>}
          {s.last_run_id && <Button size="small" component={RouterLink} to={`/chat/${s.conversation.id}`}>Open chat</Button>}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function SchedulesView() {
  const { isOwner } = useSession();
  const vm = useSchedulesViewModel();
  const [dialog, setDialog] = useState({ open: false, schedule: null });
  const [confirm, setConfirm] = useState(null);

  const save = async (fields) => {
    const ok = dialog.schedule ? await vm.update(dialog.schedule.id, fields) : await vm.create(fields);
    if (ok) setDialog({ open: false, schedule: null });
  };

  return (
    <>
      <PageHeader
        title="Schedules"
        subtitle="Messages your team receives on a timetable, such as a morning brief. They answer in the chat."
        actions={isOwner && (
          <Button variant="contained" startIcon={<AddAlarmIcon />} onClick={() => { vm.clearSaveError(); setDialog({ open: true, schedule: null }); }}>
            New schedule
          </Button>
        )}
      />
      <StateBoundary
        loading={vm.loading}
        error={vm.error}
        onRetry={vm.reload}
        isEmpty={vm.schedules.length === 0}
        empty={{
          icon: <EventRepeatIcon fontSize="large" />,
          title: 'No schedules yet',
          body: 'Ask OrkaCrew in the chat ("every morning give me a stock brief") or create one here. Scheduled runs use your AI key, so they are limited per day.',
          action: isOwner ? (
            <Button variant="contained" onClick={() => { vm.clearSaveError(); setDialog({ open: true, schedule: null }); }}>New schedule</Button>
          ) : undefined,
        }}
      >
        <Stack spacing={2}>
          {vm.schedules.map((s) => (
            <ScheduleCard
              key={s.id}
              s={s}
              isOwner={isOwner}
              busy={vm.saving}
              onToggle={() => vm.toggle(s)}
              onRun={() => vm.runNow(s.id)}
              onEdit={() => { vm.clearSaveError(); setDialog({ open: true, schedule: s }); }}
              onDelete={() => setConfirm(s)}
            />
          ))}
        </Stack>
      </StateBoundary>
      <ScheduleDialog
        open={dialog.open}
        schedule={dialog.schedule}
        chats={vm.chats}
        saving={vm.saving}
        error={vm.saveError}
        onClose={() => setDialog({ open: false, schedule: null })}
        onSave={save}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete this schedule?"
        body={`"${confirm?.title}" will stop running. Messages it already sent stay in the chat.`}
        confirmLabel="Delete"
        color="error"
        busy={vm.saving}
        onConfirm={async () => { await vm.remove(confirm.id); setConfirm(null); }}
        onClose={() => setConfirm(null)}
      />
      <Snackbar open={Boolean(vm.feedback)} autoHideDuration={6000} onClose={vm.clearFeedback} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {vm.feedback ? <Alert severity={vm.feedback.severity} onClose={vm.clearFeedback}>{vm.feedback.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
